import { useContext, useEffect, useMemo, useState } from "react";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { createFilterOptions } from "@mui/material";
import { toast } from "react-toastify";
import { UserContext } from "../../../../hooks/UserContext";
import { API } from "../../../../api";
import type { AuthUser } from "../../../../api/types";
import type { Branch, Contact, Deal, Grower, Job } from "../../../../models";
import { defaultJobForImport } from "../../../../models";
import { Oops } from "../../../../utilities/oops";
import { getDealLabel } from "../../../../utilities/utils";
import { CREATE_NEW_GROWER, CREATE_NEW_SALESPERSON } from "../../../../utilities/constants";
import { ManualEntryFormProps, ImportFormManualViewModel } from "./SingleImportForm.types";
import { commaStringToUniqueAlphanumericList } from "./SingleImportForm.helpers";

const filter = createFilterOptions<Grower | { id: string; name: string }>();

/**
 * Get default form values for job creation
 * Single source of truth for both initial form state and reset values
 */
function getDefaultFormValues(props: ManualEntryFormProps, userContext: AuthUser | undefined): Partial<Job> {
	return {
		...defaultJobForImport(),
		growerIds: [CREATE_NEW_GROWER],
		submitterEmail: userContext?.email,
		submitterName: userContext?.full_name,
		branchIds: (props.selectedBranches?.length ?? 0) > 0 ? [props.selectedBranches![0].id] : [],
		dealIds: props.selectedDeal ? [props.selectedDeal.id] : ["custom"],
		frequencyOfMicros: props.selectedDeal?.frequencyOfMicros,
		density: props.selectedDeal?.density,
		depth: props.selectedDeal?.depth,
		testPackage: props.selectedDeal?.testPackage,
	};
}

export function useImportFormManualViewModel(props: ManualEntryFormProps): ImportFormManualViewModel {
	const userContext = useContext(UserContext);

	// State
	const [growerSelected, setGrowerSelected] = useState<Grower | { id: string; name: string } | null>(null);
	const [salespersonSelected, setSalespersonSelected] = useState<Contact | { id: string; name: string } | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [deal, setDeal] = useState<Deal | { id: "custom"; label: "Create New Configuration" }>(
		props.selectedDeal ?? { id: "custom", label: "Create New Configuration" },
	);
	const [microsRaw, setMicrosRaw] = useState("");
	const [selectedBranch, setSelectedBranch] = useState<Branch | null>(
		(props.selectedBranches?.length ?? 0) > 0 ? props.selectedBranches![0] : null,
	);

	// React Hook Form
	const {
		register,
		handleSubmit,
		watch,
		control,
		reset,
		setValue,
		trigger,
		formState: { errors, isValid },
	} = useForm<Job>({
		mode: "onBlur", // Validate when field loses focus (better performance than onChange)
		reValidateMode: "onBlur", // Re-validate on blur after first submission attempt
		defaultValues: getDefaultFormValues(props, userContext),
	});

	// Watch form fields needed for validation context
	const pointsFiles = watch("files.pointsSHP");
	const salespersonIds = watch("salespersonIds");

	// Validation context for this component - reactive to points file changes and salesperson selection
	const validationContext = useMemo(() => {
		return {
			company: props.company ?? undefined,
			deal: deal.id === "custom" ? undefined : (deal as Deal),
			user: userContext ?? undefined,
			hasPointsFile: !!(pointsFiles && pointsFiles.length > 0),
			isCreatingNewSalesperson: salespersonIds?.at(0) === CREATE_NEW_SALESPERSON,
		};
	}, [props.company, deal, userContext, pointsFiles, salespersonIds]);

	// Watch deal-related fields for auto-updating deal configuration
	const frequencyOfMicros = watch("frequencyOfMicros");
	const density = watch("density");
	const depth = watch("depth");
	const testPackage = watch("testPackage");
	const dealIds = watch("dealIds");

	const updateDeal = () => {
		const currentDealId = dealIds?.at(0);
		const foundDeal = props.allDeals.find((d: Deal) => {
			return d.id === currentDealId;
		});
		if (foundDeal) {
			// Only update if deal actually changed
			if (deal.id !== foundDeal.id) {
				setDeal(foundDeal);
			}
		} else {
			// Only update if not already custom
			if (deal.id !== "custom") {
				setDeal({ id: "custom", label: "Create New Configuration" });
			}
		}
	};

	useEffect(() => {
		updateDeal();
		// Note: `deal` intentionally excluded from deps to prevent infinite loop
	}, [frequencyOfMicros, density, depth, testPackage, dealIds]);

	// Clear microsList when a pointsSHP list is emptied
	useEffect(() => {
		const subscription = watch((value, { name }) => {
			const ptsFiles = value.files?.pointsSHP;
			if (name === "files.pointsSHP" && (!ptsFiles || ptsFiles.length === 0)) {
				setMicrosRaw("");
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, setValue]);

	const getDealLabelForDropdown = (option: Deal | { id: string; label: string }) => {
		if (option && option.id === "custom") {
			return "Create New Configuration";
		}

		return getDealLabel(option as Deal);
	};

	const onSubmit: SubmitHandler<Job> = async (data) => {
		setLoading(true);
		try {
			// Validate the form before submitting
			const isFormValid = await trigger();
			if (!isFormValid) {
				toast.error("Please fill in all required fields correctly.");
				setLoading(false);
				return;
			}

			if (data.dealIds?.at(0) === "custom") {
				if (!window.confirm("Custom Configuration Selected. Proceed?")) {
					setLoading(false);
					return;
				}
			}
			if (data.dealIds?.at(0) === "custom") {
				data.dealIds = [];
			}
			if (data.growerIds?.at(0) === CREATE_NEW_GROWER) {
				data.growerIds = [];
			}
			if (data.importGrowerName && data.importGrowerName.startsWith("New Grower: ")) {
				data.importGrowerName = data.importGrowerName.replace(/^New Grower: /, "");
			}
			if (selectedBranch) {
				data.branchIds = [selectedBranch.id];
			}

			if (data.growerIds.length > 0 && data.growerIds[0] !== "" && data.growerPhoneNumber !== "") {
				await API.Data.Growers.update.phoneNumber(data.growerIds[0], data.growerPhoneNumber ?? "");
			}
			await toast.loading("Job submitting...");

			const newJob = defaultJobForImport(
				data.fieldName,
				data.depth,
				data.density,
				data.importGrowerName,
				data.frequencyOfMicros,
				data.testPackage,
				data.farmName,
				data.labName,
			);

			newJob.growerIds = data.growerIds || [];
			newJob.isTestJob = data.isTestJob || false;
			newJob.microsList = data.microsList;
			newJob.collectBoundary = data.collectBoundary;
			newJob.isPointsAttached = data.isPointsAttached || false;
			newJob.eventId = data.eventId || "";
			newJob.submitterEmail = data.submitterEmail;
			newJob.submitterName = data.submitterName;
			newJob.submissionNotes = data.submissionNotes || "";
			newJob.growerPhoneNumber = data.growerPhoneNumber;
			newJob.labInstructions = data.labInstructions || "";
			newJob.isReadyToSample = data.isReadyToSample || false;
			newJob.dealIds = data.dealIds || [];
			newJob.branchIds = data.branchIds || [];
			newJob.files = data.files || {};
			newJob.fieldId = data.fieldId || "";
			newJob.labAccountNumber = data.labAccountNumber;
			newJob.labSubmissionCode = data.labSubmissionCode;
			newJob.lastCrop = data.lastCrop;
			newJob.nextCrop = data.nextCrop;
			newJob.yieldGoal = data.yieldGoal || "";
			newJob.dateFieldReadyEstimated = data.dateFieldReadyEstimated;
			newJob.salespersonIds = data.salespersonIds || [];
			newJob.newSalesperson = data.newSalesperson;
			newJob.externalLabId = data.externalLabId || "";
			newJob.externalId = data.externalId || "";
			newJob.externalFarmId = data.externalFarmId || "";

			// If CREATE_NEW_SALESPERSON, clear the sentinel value
			if (newJob.salespersonIds.includes(CREATE_NEW_SALESPERSON)) {
				newJob.salespersonIds = [];
			}

			await API.Data.Jobs.create.one(newJob, "Created Portal Single");
			await toast.dismiss();
			setLoading(false);
			await toast.success("Job submitted successfully!");

			// Reset form to default values
			reset(getDefaultFormValues(props, userContext));
			setMicrosRaw("");
			setGrowerSelected(null);
			setSalespersonSelected(null);
			setSelectedBranch((props.selectedBranches?.length ?? 0) > 0 ? props.selectedBranches![0] : null);
			setDeal(props.selectedDeal ?? { id: "custom", label: "Create New Configuration" });
		} catch (error: any) {
			await toast.dismiss();
			Oops.handleError(error, "SubmitJobs.ManualEntryForm.onSubmit");
		} finally {
			setLoading(false);
		}
	};

	const onError: SubmitErrorHandler<Job> = (errors) => {
		watch("importGrowerName");
		toast.error("Please fill in all required fields correctly.");
		for (const errorKey of Object.keys(errors)) {
			let message = (errors as Record<string, any>)[errorKey]?.message;
			switch (errorKey) {
				case "dealIds":
				case "frequencyOfMicros":
				case "density":
				case "depth":
				case "testPackage":
					message = "Sampling Configuration Edits -> " + message;
					break;
				case "growerName":
					message = "Grower Details -> " + message;
					break;
			}
			toast.error(message);
		}
	};

	const parseMicrosList = (rawValue: string): string => {
		return commaStringToUniqueAlphanumericList(rawValue);
	};

	return {
		// State
		growerSelected,
		salespersonSelected,
		loading,
		deal,
		microsRaw,
		selectedBranch,

		// State setters
		setGrowerSelected,
		setSalespersonSelected,
		setMicrosRaw,
		setSelectedBranch,

		// React Hook Form
		register,
		handleSubmit,
		watch,
		control,
		reset,
		setValue,
		trigger,
		errors,
		isValid,

		// Computed values
		validationContext,

		// Context
		userContext,

		// Business logic functions
		updateDeal,
		getDealLabelForDropdown,
		onSubmit,
		onError,
		parseMicrosList,

		// Utilities
		filter,
	};
}
